import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { CashAccount, PaginatedCashAccounts } from '@/types/cash'
import type { CashAccountFormData } from '@/validators/cash'

export interface ListCashAccountParams {
  search?: string
  active?: boolean
  page?: number
  perPage?: number
}

const KEYS = {
  list: (params: ListCashAccountParams) => ['cash-accounts', params] as const,
  detail: (id: string) => ['cash-accounts', id] as const,
}

export function useCashAccounts(params: ListCashAccountParams = {}) {
  return useQuery({
    queryKey: KEYS.list(params),
    queryFn: () => api.get<PaginatedCashAccounts>('/cash-accounts', { params }).then((r) => r.data),
  })
}

export function useCashAccount(id: string) {
  return useQuery({
    queryKey: KEYS.detail(id),
    queryFn: () => api.get<CashAccount>(`/cash-accounts/${id}`).then((r) => r.data),
    enabled: !!id,
  })
}

export function useCreateCashAccount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CashAccountFormData) =>
      api.post<CashAccount>('/cash-accounts', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cash-accounts'] }),
  })
}

export function useUpdateCashAccount(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CashAccountFormData) =>
      api.put<CashAccount>(`/cash-accounts/${id}`, data).then((r) => r.data),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ['cash-accounts'] })
      qc.setQueryData(KEYS.detail(id), updated)
    },
  })
}

export function useUpdateOpeningBalance(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (openingBalance: number) =>
      api
        .patch<CashAccount>(`/cash-accounts/${id}/opening-balance`, { openingBalance })
        .then((r) => r.data),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ['cash-accounts'] })
      qc.setQueryData(KEYS.detail(id), updated)
    },
  })
}

export function useDeactivateCashAccount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api.patch<CashAccount>(`/cash-accounts/${id}/deactivate`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cash-accounts'] }),
  })
}

export function useActivateCashAccount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api.patch<CashAccount>(`/cash-accounts/${id}/activate`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cash-accounts'] }),
  })
}
